/**
 * Power BI Configuration Service
 *
 * Fetches Power BI configuration from the backend API.
 * All configuration is server-side - no frontend env vars needed.
 */
import axios from '@/plugins/request';

export interface PowerBiReportPage {
  pageKey: string;
  pageName: string;
  displayName: string;
  category: string;
  icon: string;
  permissionKey: string | null;
  sequence: number;
}

export interface PowerBiReport {
  reportKey: string;
  reportId: string;
  datasetId: string | null;
  workspaceId: string | null;
  displayName: string;
  description: string | null;
  isDefault: boolean;
  sequence: number;
  pages: PowerBiReportPage[];
}

export interface PowerBiConfig {
  tenantId: string;
  baseEmbedUrl: string;
  refreshCooldownMinutes: number;
  lastRefreshTime: string | null;
  canRefresh: boolean;
  reports: PowerBiReport[];
}

export interface RefreshResult {
  message: string;
  estimatedCompletion: string | null;
}

export interface RefreshStatus {
  lastRefreshTime: string | null;
  lastRefreshStatus: string;
  canRefresh: boolean;
  cooldownRemainingMinutes: number | null;
}

/**
 * SignalR notification received when a Power BI dataset refresh completes.
 */
export interface PowerBiRefreshNotification {
  datasetId: string;
  status: string;
  errorMessage: string | null;
  triggeredByUserId: number;
  triggeredByUsername: string | null;
  triggeredAt: string;
  completedAt: string | null;
  durationSeconds: number | null;
}

class PowerBiConfigService {
  private config: PowerBiConfig | null = null;
  private configPromise: Promise<PowerBiConfig> | null = null;

  /**
   * Fetches Power BI config from backend.
   * Caches the result after first successful call.
   */
  async getConfig(): Promise<PowerBiConfig> {
    if (this.config) {
      return this.config;
    }

    // Prevent multiple simultaneous requests
    if (this.configPromise) {
      return this.configPromise;
    }

    this.configPromise = axios
      .get<PowerBiConfig>('/api/reports/powerbi/config')
      .then((response) => {
        this.config = response.data;
        return this.config;
      })
      .finally(() => {
        this.configPromise = null;
      });

    return this.configPromise;
  }

  /**
   * Returns cached config if available, null otherwise.
   * Does not fetch from API - use getConfig() to fetch.
   */
  getCachedConfig(): PowerBiConfig | null {
    return this.config;
  }

  /**
   * Clears the cached config.
   * Call when user logs out or when config might have changed.
   */
  clearCache(): void {
    this.config = null;
  }

  /**
   * Triggers a Power BI dataset refresh.
   * Admin only. Subject to cooldown and Power BI rate limits.
   */
  async refreshDataset(): Promise<RefreshResult> {
    const response = await axios.post<RefreshResult>('/api/reports/powerbi/refresh');
    // Clear cache so next getConfig() fetches fresh lastRefreshTime
    this.clearCache();
    return response.data;
  }

  /**
   * Gets current refresh status including last refresh time and whether refresh is available.
   */
  async getRefreshStatus(): Promise<RefreshStatus> {
    const response = await axios.get<RefreshStatus>('/api/reports/powerbi/refresh/status');
    return response.data;
  }

  /**
   * Gets the default report from the config.
   * Returns the first report marked as default, or the first report if none is default.
   */
  getDefaultReport(): PowerBiReport | null {
    if (!this.config || this.config.reports.length === 0) {
      return null;
    }
    return this.config.reports.find((r) => r.isDefault) || this.config.reports[0];
  }

  /**
   * Gets a report by its key.
   */
  getReportByKey(reportKey: string): PowerBiReport | undefined {
    return this.config?.reports.find((r) => r.reportKey === reportKey);
  }

  /**
   * Ensures config is loaded, fetching it if necessary.
   * Returns true if config is available, false if fetch failed.
   */
  async ensureConfig(): Promise<boolean> {
    if (this.config) {
      return true;
    }
    try {
      await this.getConfig();
      return this.config !== null;
    } catch {
      return false;
    }
  }

  /**
   * Checks if config is currently loaded.
   */
  isConfigLoaded(): boolean {
    return this.config !== null;
  }

  /**
   * Builds the embed URL for a specific report page.
   * Returns empty string if config is not loaded (use ensureConfig() first for guaranteed results).
   *
   * @param reportId - The Power BI report GUID
   * @param pageName - The Power BI internal page identifier
   * @returns The full embed URL for the iframe, or empty string if config not loaded
   */
  buildEmbedUrl(reportId: string, pageName: string): string {
    if (!this.config) {
      return '';
    }

    const params = new URLSearchParams({
      reportId: reportId,
      autoAuth: 'true',
      ctid: this.config.tenantId,
      pageName: pageName,
      filterPaneEnabled: 'false',
      navContentPaneEnabled: 'false',
      actionBarEnabled: 'false',
    });

    return `${this.config.baseEmbedUrl}?${params.toString()}`;
  }

  /**
   * Builds the direct Power BI URL for opening in a new tab.
   * Returns empty string if config is not loaded.
   *
   * @param reportId - The Power BI report GUID
   * @param pageName - The Power BI internal page identifier
   * @returns The direct Power BI URL, or empty string if config not loaded
   */
  buildDirectUrl(reportId: string, pageName: string): string {
    if (!this.config) {
      return '';
    }

    return `https://app.powerbi.com/groups/me/reports/${reportId}/${pageName}?experience=power-bi`;
  }
}

// Export singleton instance
export const powerBiConfigService = new PowerBiConfigService();
export default powerBiConfigService;
